import {
  ecartNiveau,
  formatDecimal,
  formatInteger,
  pluralWord,
  principalReappro,
  principalReapproDay,
  type EcartNiveau,
} from '@gozoki/tools'
import { DateTime } from 'luxon'
import type { PilotagePerimetre } from '../-search-schema'
import { formatCategoryLabel } from '../-reappro/reappro-format'
import { formatCoefValue } from '../-shared/coef-slider-scale'
import { pilotageHourLabel, pilotageInstantLabel } from '../-shared/pilotage-call-track'
import { formatSignedPercent } from '../-shared/signed-percent'
import { parsePilotageModalKey, pilotageModalKey } from '../-shared/pilotage-modal-navigation'
import type {
  AlerteAcceptation,
  AlerteType,
  Modificateur,
  ModificateurCreation,
  ModificateurDuree,
  ModificateurType,
  PilotageAppel,
  PilotagePerimetreInfo,
  PrevisionJour,
  PrevisionCategorie,
  PrevisionResponse,
  PrevisionTemporalite,
  TemporaliteCategorieReappro,
  TemporaliteJour,
} from './prevision-contract'

/**
 * Logique pure de la prévision du Pilotage, sans React : formats, niveau d'alerte d'un écart,
 * acceptations, temporalité des appels, modificateurs. Les alertes à traiter viennent du serveur
 * (route alertes de R-DATA). Testée par `prevision-model.test.mjs`.
 */

export const MISSING = '—'

/**
 * UVC : entier groupé (« 2 850 »), une décimale sous 10 quand il y en a une (« 5,7 », mais « 6 »
 * et « 0 ») ; `null` : « — ».
 */
export function formatUvc(uvc: number | null): string {
  if (uvc === null) return MISSING
  const tenths = Math.round(uvc * 10)
  if (Math.abs(uvc) < 10 && tenths % 10 !== 0) return formatDecimal(tenths / 10, 1)
  return formatInteger(Math.round(uvc))
}

/** Écart signé arrondi à l'unité : « +9 % », « −11 % », « 0 % » ; `null` : « — ». */
export function formatEcart(pct: number | null): string {
  return pct === null ? MISSING : formatSignedPercent(Math.round(pct))
}

/** Volatilité : « ±13 % » ; `null` : « — ». */
export function formatVolatilite(pct: number | null): string {
  return pct === null ? MISSING : `±${formatInteger(Math.round(pct))} %`
}

/** Taux : « 8,0 % » (une décimale) ou « 16 % » ; `null` : « — ». */
export function formatTaux(pct: number | null, decimals: 0 | 1 = 0): string {
  if (pct === null) return MISSING
  return `${decimals === 1 ? formatDecimal(pct, 1) : formatInteger(Math.round(pct))} %`
}

/** Coef de sécurité : « 1,60 ». */
export function formatCoef(coef: number | null): string {
  return coef === null ? MISSING : formatCoefValue(coef)
}

/** « réseau, 47 boutiques », ou le libellé de la boutique. */
export function perimetreLabel(perimetre: PilotagePerimetreInfo): string {
  if (perimetre.type === 'boutique') return perimetre.libelle
  return `réseau, ${perimetre.boutiques} ${pluralWord(perimetre.boutiques, 'boutique')}`
}

/**
 * Libellé d'une catégorie racine : la base les range en majuscules (« ENCAS SUCRES »), l'écran les
 * montre « Encas sucres », comme la vue Réappro et le dashboard réseau.
 */
export const withCategorieLibelle = <T extends { libelle: string }>(row: T): T => ({
  ...row,
  libelle: formatCategoryLabel(row.libelle),
})

/** Libellé d'une référence, suivi de son EAN quand une autre porte le même libellé. */
export const referenceLibelle = (row: {
  libelle: string
  reference: string
  libelleEnDouble: boolean
}): string => (row.libelleEnDouble ? `${row.libelle} · ${row.reference}` : row.libelle)

// ─── Jours ───────────────────────────────────────────────────────────────────

/** « mardi ». */
export function jourLongLabel(iso: string): string {
  return DateTime.fromISO(iso).setLocale('fr').toFormat('cccc')
}

/** Badge d'un jour de l'en-tête : férié, ou premier jour après des vacances scolaires. */
export function jourBadge(jours: readonly PrevisionJour[], index: number): string | null {
  const jour = jours[index]
  if (jour === undefined) return null
  if (jour.ferie !== null) return 'Férié'
  const veille = jours[index - 1]
  return veille?.vacancesScolaires && !jour.vacancesScolaires ? 'Rentrée' : null
}

// ─── Volatilité ──────────────────────────────────────────────────────────────

/** Ordre du tableau (références d'une catégorie, boutiques) : prévision décroissante. */
export const parPrevisionDecroissante = <T extends { prevueUvc: number }>(
  lignes: readonly T[],
): T[] => [...lignes].sort((a, b) => b.prevueUvc - a.prevueUvc)

// ─── Alertes ─────────────────────────────────────────────────────────────────

/** Alerte d'écart avec les ventes S-1 : règle partagée avec l'écran unique (`@gozoki/tools`). */
export { ecartNiveau, type EcartNiveau }

/** La ligne d'une alerte : une catégorie, ou une de ses références (`reference`). */
export interface AlerteLigne {
  categorieId: number
  reference: string | null
}

/**
 * Les boutiques sur lesquelles la page mesure ses alertes : la boutique choisie, ou au réseau celles
 * que L2 mesure (`perimetre.storeIds` : les boutiques autorisées qui ont une prévision officielle).
 * Jamais `reseau` dans le corps : pour un admin limité à certaines boutiques, L1 le refuse (403),
 * alors que la page réseau agrège ses boutiques.
 */
export function boutiquesDePage(
  perimetre: PilotagePerimetre,
  storeIdsMesures: readonly number[],
): number[] {
  if (perimetre !== 'reseau') return [perimetre]
  return [...storeIdsMesures].sort((a, b) => a - b)
}

/** Borne de `valeurPct` dans le schéma de L1 : un écart au-delà (S-1 presque nul) est tracé à la borne. */
const ACCEPTATION_VALEUR_MAX = 1000

/**
 * Corps de « Accepter » (L1, § 4.4) pour l'alerte affichée d'une ligne un jour donné : cette ligne
 * (catégorie ou référence), sur les boutiques de la page (`boutiquesDePage`), où l'alerte a été
 * mesurée.
 */
export function acceptationOf({
  type,
  valeurPct,
  ligne,
  boutiques,
  date,
}: {
  type: AlerteType
  valeurPct: number | null
  ligne: AlerteLigne
  boutiques: readonly number[]
  date: string
}): AlerteAcceptation {
  return {
    alerte: type,
    cible: cibleDeLigne(ligne),
    boutiques: [...boutiques],
    dates: [date],
    ...(valeurPct === null
      ? {}
      : {
          valeurPct: Math.max(
            -ACCEPTATION_VALEUR_MAX,
            Math.min(ACCEPTATION_VALEUR_MAX, Math.round(valeurPct)),
          ),
        }),
  }
}

/**
 * Clé de session d'une acceptation, tirée du corps envoyé : l'écran retient exactement ce que L1 a
 * tracé (type, cible, boutiques, jours).
 */
export const acceptationKey = (
  acceptation: Pick<AlerteAcceptation, 'alerte' | 'cible' | 'boutiques' | 'dates'>,
): string =>
  JSON.stringify([acceptation.alerte, acceptation.cible, acceptation.boutiques, acceptation.dates])

const reapproLookup =
  (temporalite: PrevisionTemporalite) =>
  (id: number): TemporaliteCategorieReappro | undefined =>
    temporalite.categoriesReappro.find((reappro) => reappro.id === id)

/** La catégorie de réappro principale d'une catégorie (`principalReappro`, règle du serveur). */
const reapproPrincipaleOf = (
  temporalite: PrevisionTemporalite,
  categorie: PrevisionCategorie,
): TemporaliteCategorieReappro | undefined =>
  principalReappro(reapproLookup(temporalite), categorie.categoriesReappro)

/**
 * La temporalité d'une case catégorie × jour (`principalReapproDay`, règle partagée avec les
 * alertes du serveur) : son `fige` dit si la case est figée, case par case.
 */
export function temporaliteJourOf(
  temporalite: PrevisionTemporalite,
  categorie: PrevisionCategorie,
  date: string,
): TemporaliteJour | undefined {
  return principalReapproDay(reapproLookup(temporalite), categorie.categoriesReappro, date)?.jour
}

/** Pourquoi un jour ne se corrige ni ne se projette plus : le vocabulaire du tableau. */
export const JOUR_FIGE_LABEL = 'Jour figé : son appel est passé'

/**
 * Jours qu'un appel passé a déjà lus : ceux de la catégorie, ou, sans catégorie, ceux d'une catégorie
 * au moins (« jour figé »).
 */
export function joursFiges(
  prevision: Pick<PrevisionResponse, 'categories' | 'jours' | 'temporalite'>,
  categorie?: PrevisionCategorie,
): ReadonlySet<string> {
  const categories = categorie === undefined ? prevision.categories : [categorie]
  const figes = prevision.jours.filter(({ date }) =>
    categories.some((item) => temporaliteJourOf(prevision.temporalite, item, date)?.fige),
  )
  return new Set(figes.map((jour) => jour.date))
}

/** Dates des jours passés d'une lecture, au statut du serveur. */
export const joursPasses = (
  jours: readonly Pick<PrevisionJour, 'date' | 'statut'>[],
): ReadonlySet<string> =>
  new Set(jours.filter((jour) => jour.statut === 'passe').map((jour) => jour.date))

/**
 * Une case est figée si son jour est passé, même sorti du calendrier des appels, ou si son appel l'a
 * lue (`fige` de `temporaliteJourOf`).
 */
export const caseFigee = (
  passe: boolean,
  temporalite: Pick<TemporaliteJour, 'fige'> | undefined,
): boolean => passe || (temporalite?.fige ?? false)

/** Ce que dit d'une case son jour, et la temporalité de sa catégorie ce jour-là. */
export interface EtatDeCase {
  fige: boolean
  passe: boolean
  /** Absente, l'appel n'est pas nommé. */
  temporalite?: TemporaliteJour
}

/** « appel du mar. 03/11 à 14 h, encore 2 recalculs avant », ou sa dernière prévision. */
function lectureParAppel(
  { previsionDerniere, recalculsRestants }: TemporaliteJour,
  commandeLe: string,
): string {
  const appel = `appel du ${pilotageInstantLabel(commandeLe)}`
  if (previsionDerniere) return `${appel}, dernière prévision avant lui`
  if (!recalculsRestants) return appel
  return `${appel}, encore ${recalculsRestants} ${pluralWord(recalculsRestants, 'recalcul')} avant`
}

/**
 * Fin de l'infobulle d'une case : jour passé, jour figé avec l'appel qui l'a lu, ou l'appel qui le
 * lira et les recalculs qui restent avant lui. La seule lecture par appel de l'écran principal.
 */
export function etatDeCase({ fige, passe, temporalite }: EtatDeCase): string {
  const commandeLe = temporalite?.appel?.commandeLe
  if (passe) return 'jour passé'
  if (fige) {
    return commandeLe
      ? `jour figé : l’appel de ${pilotageHourLabel(commandeLe)} l’a lu`
      : 'jour figé'
  }
  if (temporalite && commandeLe) {
    return `${lectureParAppel(temporalite, commandeLe)} · ouvrir le détail`
  }
  return 'ouvrir le détail'
}

// ─── Modales d'un élément ────────────────────────────────────────────────────

/** Clé d'URL de la modale d'une catégorie : `categorie:12`. */
export const categorieModalKey = (categorieId: number): string =>
  pilotageModalKey('categorie', categorieId)

/** Clé d'URL de la modale d'une référence, avec sa catégorie : `reference:12:3760373405068`. */
export const referenceModalKey = (categorieId: number, reference: string): string =>
  pilotageModalKey('reference', `${categorieId}:${reference}`)

export type PrevisionModalTarget =
  | { kind: 'categorie'; categorieId: number }
  | { kind: 'reference'; categorieId: number; reference: string }

const POSITIVE_ID = /^[1-9]\d*$/

/** La catégorie (ou la référence) d'une clé d'URL de modale, `null` pour une autre modale. */
export function parsePrevisionModal(modale: string | undefined): PrevisionModalTarget | null {
  if (modale === undefined) return null
  const { kind, id } = parsePilotageModalKey(modale)
  if (id === null) return null
  if (kind === 'categorie') {
    return POSITIVE_ID.test(id) ? { kind, categorieId: Number(id) } : null
  }
  if (kind !== 'reference') return null
  const separator = id.indexOf(':')
  const categorieId = id.slice(0, separator)
  const reference = id.slice(separator + 1)
  if (separator === -1 || !POSITIVE_ID.test(categorieId) || reference === '') return null
  return { kind, categorieId: Number(categorieId), reference }
}

// ─── Modificateurs posés (L1) ────────────────────────────────────────────────

/*
 * La règle de L1 (§ 1) appliquée à la liste des modificateurs actifs, seule copie côté écran tant
 * que L2 ne rend pas le multiplicateur appliqué par boutique : sur une cellule, le plus récent des
 * actifs qui la couvrent gagne, seul. Les trois lectures de l'écran (ligne d'une boutique, point du
 * tableau, départ du curseur) passent par `couvre` et `plusRecent`.
 */

/** Une cellule : catégorie (ou une de ses références) × boutique × jour. */
export interface ModificateurCellule {
  type: ModificateurType
  categorieId: number
  /** Référence de la cellule ; `null` : la catégorie, sans ses références. */
  reference: string | null
  /** Boutique de la cellule ; `null` : n'importe laquelle. */
  storeId: number | null
  date: string
}

const isoWeekdayOf = (date: string): number => DateTime.fromISO(date).weekday

function coversDate(modificateur: Modificateur, date: string): boolean {
  if (modificateur.duree === 'prochain_appel') {
    return modificateur.prochainAppel?.jours.includes(date) ?? false
  }
  const { jours } = modificateur
  if (date < jours.debut || (jours.fin !== null && date > jours.fin)) return false
  return jours.joursSemaine === null || jours.joursSemaine.includes(isoWeekdayOf(date))
}

function coversCible(modificateur: Modificateur, cellule: ModificateurCellule): boolean {
  const { cible } = modificateur
  if (cible.type === 'categorie') return cible.categorieId === cellule.categorieId
  return cellule.reference !== null && cible.reference === cellule.reference
}

/** Le modificateur actif couvre la cellule : type, cible (référence ou sa catégorie), boutique, jour. */
function couvre(modificateur: Modificateur, cellule: ModificateurCellule): boolean {
  return (
    modificateur.statut === 'actif' &&
    modificateur.type === cellule.type &&
    coversCible(modificateur, cellule) &&
    (cellule.storeId === null ||
      modificateur.boutiques.reseau ||
      modificateur.boutiques.liste.some((store) => store.id === cellule.storeId)) &&
    coversDate(modificateur, cellule.date)
  )
}

/** Le plus récent des modificateurs retenus (règle de L1 : jamais de produit de deux). */
function plusRecent(
  modificateurs: readonly Modificateur[],
  retenu: (modificateur: Modificateur) => boolean,
): Modificateur | null {
  let winner: Modificateur | null = null
  for (const modificateur of modificateurs) {
    if (!retenu(modificateur)) continue
    if (winner === null || modificateur.id > winner.id) winner = modificateur
  }
  return winner
}

// ─── Corrections (multiplicateur, coef) ─────────────────────────────────────

/** Où porte une correction : un article (sinon la catégorie), sur des boutiques ou le réseau. */
export interface PorteeCorrection {
  categorieId: number
  /** Référence de l'article ; `null` : la catégorie. */
  reference: string | null
  boutiques: 'reseau' | readonly number[]
}

/** La cible de L1 (modificateur ou acceptation) d'une ligne : sa référence, sinon sa catégorie. */
function cibleDeLigne(ligne: AlerteLigne): ModificateurCreation['cible'] {
  return ligne.reference === null
    ? { categorieId: ligne.categorieId }
    : { reference: ligne.reference }
}

/**
 * Jours d'un modificateur posé depuis le jour choisi. « Prochain appel » : aucun, le serveur prend
 * tous les jours que lit le prochain appel (le samedi et le dimanche d'un appel qui couvre le
 * week-end, RETOURS § 1). « Jusqu'à nouvel ordre » : à partir du jour choisi.
 */
function decisionJours(
  duree: ModificateurDuree,
  date: string,
): ModificateurCreation['jours'] | undefined {
  return duree === 'prochain_appel' ? undefined : { debut: date, fin: null }
}

/** Corps d'un multiplicateur (L1, `POST /forecast/pilotage/modificateurs`), seul écrit par l'écran. */
export function modificateurCreationOf({
  valeur,
  portee,
  duree,
  date,
}: {
  valeur: number
  portee: PorteeCorrection
  duree: ModificateurDuree
  date: string
}): ModificateurCreation {
  const jours = decisionJours(duree, date)
  return {
    type: 'multiplicateur',
    valeur,
    cible: cibleDeLigne(portee),
    boutiques: portee.boutiques === 'reseau' ? 'reseau' : [...portee.boutiques],
    duree,
    ...(jours === undefined ? {} : { jours }),
  }
}

/**
 * Le prochain appel de la catégorie de réappro principale d'une catégorie, et le premier jour qu'il
 * lit (ses recalculs restants, « dernière prévision ») ; `null` sans appel à venir.
 */
export function prochainAppelDe(
  temporalite: PrevisionTemporalite,
  categorie: PrevisionCategorie,
): { appel: PilotageAppel; jour: TemporaliteJour | undefined } | null {
  const reappro = reapproPrincipaleOf(temporalite, categorie)
  if (!reappro?.prochainAppel) return null
  const appel = reappro.prochainAppel
  const jour = reappro.jours.find((item) => !item.fige && item.appel?.livraison === appel.livraison)
  return { appel, jour }
}

/**
 * Le jour est lu par le prochain appel de la catégorie de réappro principale : la durée « prochain
 * appel » n'a de sens que pour lui. Un jour figé (appel passé) ne l'est jamais.
 */
export function luParProchainAppel(
  temporalite: PrevisionTemporalite,
  categorie: PrevisionCategorie,
  date: string,
): boolean {
  const reappro = reapproPrincipaleOf(temporalite, categorie)
  const jour = reappro?.jours.find((item) => item.date === date)
  if (!jour?.appel || jour.fige || !reappro?.prochainAppel) return false
  return jour.appel.livraison === reappro.prochainAppel.livraison
}

/**
 * Valeur de départ du curseur « Ajuster la prévision » : le multiplicateur actif posé exactement sur
 * cette cible et ces boutiques et qui couvre le jour, sinon 0 (prévision du moteur).
 */
export function multiplicateurDeDepart(
  modificateurs: readonly Modificateur[],
  portee: PorteeCorrection,
  date: string,
): number {
  const { reference, boutiques } = portee
  const cellule: ModificateurCellule = {
    type: 'multiplicateur',
    categorieId: portee.categorieId,
    reference,
    storeId: null,
    date,
  }
  const winner = plusRecent(
    modificateurs,
    (modificateur) =>
      couvre(modificateur, cellule) &&
      modificateur.cible.reference === reference &&
      sameBoutiques(modificateur, boutiques),
  )
  return winner?.valeur ?? 0
}

function sameBoutiques(
  modificateur: Modificateur,
  boutiques: 'reseau' | readonly number[],
): boolean {
  if (boutiques === 'reseau') return modificateur.boutiques.reseau
  if (modificateur.boutiques.reseau) return false
  const ids = new Set(modificateur.boutiques.liste.map((store) => store.id))
  return ids.size === boutiques.length && boutiques.every((id) => ids.has(id))
}
